import {
  MarkerClusterer,
  SuperClusterAlgorithm,
} from "@googlemaps/markerclusterer";
import { memo, useEffect, useRef, useState } from "react";

const getBearing = (prev, next) => {
  if (!prev || !next) return 0;

  const lat1 = prev.lat * (Math.PI / 180);
  const lat2 = next.lat * (Math.PI / 180);
  const dLon = (next.long - prev.long) * (Math.PI / 180);

  const y = Math.sin(dLon) * Math.cos(lat2);
  const x =
    Math.cos(lat1) * Math.sin(lat2) -
    Math.sin(lat1) * Math.cos(lat2) * Math.cos(dLon);

  return ((Math.atan2(y, x) * 180) / Math.PI + 360) % 360;
};

const DriverMarkers = ({
  drivers,
  onSelect,
  followTargetId,
  mapRef,
  selectedDriver,
  updateSelectedDriver,
}) => {
  const markersRef = useRef({});
  const [clusterer, setClusterer] = useState(null);

  useEffect(() => {
    if (!mapRef.current || !window.google) return;

    const newClusterer = new MarkerClusterer({
      map: mapRef.current,
      algorithm: new SuperClusterAlgorithm({ radius: 80 }),
    });

    setClusterer(newClusterer);

    return () => newClusterer?.clearMarkers();
    // eslint-disable-next-line
  }, [mapRef]);

  useEffect(() => {
    if (!clusterer) return;

    Object.keys(markersRef.current).forEach((id) => {
      const stillExists = drivers.some((d) => d.driverId === id);

      if (!stillExists) {
        const { marker } = markersRef.current[id];
        clusterer.removeMarker(marker);
        marker.map = null;
        delete markersRef.current[id];
      }
    });
    // eslint-disable-next-line
  }, [drivers, clusterer]);

  useEffect(() => {
    if (!clusterer || !window.google?.maps?.marker?.AdvancedMarkerElement)
      return;

    drivers.forEach((driver) => {
      const lat = Number(driver.location?.lat);
      const long = Number(driver.location?.long);
      if (isNaN(lat) || isNaN(long)) return;

      const exists = markersRef.current[driver.driverId];

      if (!exists) {
        const img = document.createElement("img");
        img.style.width = "55px";
        img.style.transition = "transform .25s linear";
        img.style.willChange = "transform";

        const marker = new window.google.maps.marker.AdvancedMarkerElement({
          position: { lat, lng: long },
          content: img,
          map: mapRef.current,
          title: driver?.driverName,
        });

        marker.addListener("click", () => {
          onSelect(driver.driverId);
        });

        markersRef.current[driver.driverId] = { marker, img };

        clusterer.addMarker(marker);
      }

      const { marker, img } = markersRef.current[driver.driverId];

      let icon = "assets/markers/pickup_driver.png";
      if (driver.orders?.length > 0) {
        icon = driver.orders.some((o) => o.status === "5")
          ? "assets/markers/en_route_driver.png"
          : "assets/markers/pickup_driver.png";
      }
      img.src = `${process.env.REACT_APP_PUBLIC_URL}${icon}`;

      img.style.transform = `rotate(${getBearing(
        driver.prevLocation,
        driver.location
      )}deg)`;

      marker.position = { lat, lng: long };

      marker.map = mapRef.current;
      if (!clusterer.markers.includes(marker)) clusterer.addMarker(marker);

      if (followTargetId === driver.driverId && mapRef.current) {
        mapRef.current.panTo({ lat, lng: long });
      }

      if (
        selectedDriver?.driverId === driver.driverId &&
        updateSelectedDriver
      ) {
        updateSelectedDriver({
          ...driver,
          _marker: marker,
          _position: { lat, long },
        });
      }
    });
    // eslint-disable-next-line
  }, [
    drivers,
    clusterer,
    followTargetId,
    selectedDriver,
    updateSelectedDriver,
  ]);

  return null;
};

export default memo(DriverMarkers);
